import type { UserOut as IUser } from '@repo/api-codegen/v2';
import { get, isArray } from 'lodash-es';
import { isJson } from '../../utils/utils';

/**
 * Extracts owner IDs from a record field, handling both stringified arrays
 * and regular arrays, as well as object references.
 */
export function extractOwnerIds(
	record: Record<string, unknown>,
	field: string
): string[] {
	const fieldValue = get(record, field);

	if (!fieldValue) {
		return [];
	}

	// Handle stringified JSON arrays (custom properties)
	const value = isJson(fieldValue as string)
		? JSON.parse(fieldValue as string)
		: fieldValue;

	// Ensure it's an array
	const arrayValue = isArray(value) ? value : [];

	// Extract IDs, handling both string IDs and object references
	return arrayValue
		.map((item) => (typeof item === 'object' ? (item as IUser).id : item))
		.filter((id): id is string => typeof id === 'string');
}

/**
 * Extracts both user owner IDs and group IDs from a record.
 */
export function extractOwnerAndGroupIds(
	record: Record<string, unknown>,
	ownerField: string = 'owners',
	groupField: string = 'owners_groups'
): { ownerIds: string[]; groupIds: string[] } {
	return {
		ownerIds: extractOwnerIds(record, ownerField),
		groupIds: extractOwnerIds(record, groupField),
	};
}
